<template>
    <div style="padding-bottom: 50px">
        <div v-if="loading">
            <AppPageLoader text="Loading User..." />
        </div>
        <div v-else>
            <page-header
                :title="fullName" :subtitle="user ? 'User' : null"
                editable
                deletable
                @delete="deleteUserDialogOpen = true"
                @edit="editUserDialogOpen = true"
            >
                <template #title-right v-if="user">
                    <div v-if="!isCovidAccount && !isPatientAccount">
                        <app-chip label color="blue" v-if="isAdmin">Admin</app-chip>
                        <app-chip label color="green" v-else>User</app-chip>
                    </div>
                    <div v-else>
                        <app-chip label color="red" v-if="isCovidAccount">COVID-19 Account</app-chip>
                        <app-chip label color="red" v-if="isPatientAccount">Patient Account</app-chip>
                    </div>
                </template>
            </page-header>

            <app-card title="User Information">
                <div class="pa-4">
                    <div style="display: flex; flex-direction: row" v-for="(field, index) in fields" :key="index">
                        {{ field.label }}: <strong class="pl-2">{{ field.value }}</strong>
                    </div>
                </div>
            </app-card>

            <UserOrganizationsCard
                v-if="isEnterpriseView && user"
                :account="user"
                :editable="!isEditingSelf || canManageOwnOrgRole"
                @org-role-changed="onOrgRoleChanged"
                @orgs-saved="onOrgsSaved"
            />

            <app-card v-if="!isCovidAccount && !isPatientAccount" title="User Groups" class="mt-4">
                <template #header-right>
                    <span v-if="!editingUserGroups && !isAdmin">
                        <app-fab-btn iconName="icon-edit" color="white" x-small @click="editingUserGroups = true"/>
                    </span>
                </template>
                <div class="pa-4">
                    <div v-if="isAdmin" class="system-admin-groups-message">
                        <v-icon color="info" class="mr-2">mdi-information</v-icon>
                        <span>System Admins automatically have access to all groups within the organization.</span>
                    </div>
                    <div v-else-if="!editingUserGroups">
                        <div v-for="group in userGroups" :key="group.id"
                             style="border: 1px solid lightgrey; border-radius: 5px; margin: 5px 0;" class="pa-2">
                            {{ group.name }}
                        </div>
                        <div v-if="userGroups.length === 0">
                            There are no groups assigned to this user
                        </div>
                    </div>
                    <div v-else>
                        <AppMultipleSelect
                            v-model="editUserForm.groupIds"
                            :items="groups"
                            item-text="name"
                            item-value="id"
                            item-title="name"
                        >
                        </AppMultipleSelect>
                        <div class="mt-2">
                            <app-btn @click="submitEditUserGroups" :loading="savingUserGroups">Save Groups</app-btn>
                            <app-btn outlined class="ml-2" @click="editingUserGroups = false">Cancel</app-btn>
                        </div>
                    </div>
                </div>
            </app-card>

            <app-confirm-dialog
                :open="deleteUserDialogOpen"
                @close="deleteUserDialogOpen = false"
                title="Delete User"
                @confirm="deleteUser"
                confirm-delete
                :loading="deletingUser"
            >
                Are you sure you want to delete user "{{ fullName }}"?
            </app-confirm-dialog>

            <dialog-form
                :open="editUserDialogOpen"
                title="Edit User"
                @close="editUserDialogOpen = false"
                @submit="submitEditUserForm"
                :width="600"
                :saving="savingUser"
            >
                <template v-slot:form-content>
                    <add-user-form :new-user-form="editUserForm" :is-existing-user="true" :disable-access-type="isEditingSelf"></add-user-form>
                </template>
            </dialog-form>
        </div>
    </div>
</template>

<script>
import {
    getAccount,
    switchSelectedOrganization,
    updateAccount,
    updateAccountModel,
    checkEmailAddress,
    deleteAccount,
    updateAccountGroupsModel, updateAccountGroups
} from '@/services/userService'
import {SET_SNACKBAR, SET_USER} from "@/store/mutation-types";
import AddUserForm from "@/components/UserPages/AddUserForm";
import UserOrganizationsCard from "@/components/UserOrganizationsCard";
import {getGroups, getGroupsForEnterpriseAccount} from "@/services/groups.service";
import {isAdminOrgAccessType, isCovidAccount, isPatientAccount} from "@/js/shared/helper";
import {getProfessionalTypeById} from "@/js/utilities"

export default {
    name: "User",
    components: {
        AddUserForm,
        UserOrganizationsCard
    },
    data() {
        return {
            loading: false,
            user: null,
            userGroups: [],
            groups: [],
            userOrganizations: [],
            editingUserGroups: false,
            deleteUserDialogOpen: false,
            deletingUser: false,
            editUserDialogOpen: false,
            savingUser: false,
            savingUserGroups: false,
            editUserForm: {
                email: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                jobTitle: '',
                isSafetyCoach: false,
                professionalTypeId: null,
                organizationAccessTypeId: 1,
                groupIds: [],
                organizations: [],
                organizationIds: [],
                isBillingContact: false,
                optimizedSyncTimePeriodDays: 0,
                isOptimizedSyncEnabled: false
            },
        }
    },
    async mounted() {
        await this.initializeUser()

        this.getGroups()
    },
    watch: {
        $route(to, from) {
            if (to.params.id !== from.params.id) {
                this.clearUser()
                this.initializeUser()
            }
        },
    },

    methods: {
        async getGroups() {
            let response = null;

            if (!this.$store.getters.isEnterpriseAdminOrg) {
                response = await getGroups();
            } else {
                response = await getGroupsForEnterpriseAccount(this.$route.params.id);
            }

            if (!response.isSuccess) {
                return
            }
            this.groups = response.groups;
        },
        async initializeUser() {
            this.loading = true;
            this.$store.commit(SET_USER, null)

            const response = await getAccount(this.$route.params.id);
            if (!response.isSuccess) {
                this.loading = false;
                return
            }
            this.user = response.account;
            this.userGroups = response.groups;
            this.userOrganizations = response.organizations;
            this.$store.commit(SET_USER, this.user)
            this.initEditUserForm()
            this.loading = false;
        },
        initEditUserForm() {
            const {
                email,
                firstName,
                lastName,
                phoneNumber,
                jobTitle,
                isSafetyCoach,
                professionalTypeId,
                selectedOrganizationAccessTypeId,
                isBillingContact,
                isOptimizedSyncEnabled,
                optimizedSyncTimePeriodDays
            } = this.user

            let organizations = [];
            let groupIds = [];

            if (this.isEnterpriseView)
                organizations = this.userOrganizations;

            groupIds = this.userGroups.map(group => group.id);

            this.editUserForm = {
                email,
                firstName,
                lastName,
                phoneNumber,
                jobTitle,
                isSafetyCoach,
                professionalTypeId,
                organizationAccessTypeId: selectedOrganizationAccessTypeId,
                groupIds,
                organizations,
                organizationIds: organizations.map(o => o.id),
                isBillingContact,
                isOptimizedSyncEnabled,
                optimizedSyncTimePeriodDays
            };
        },
        clearUser() {
            this.user = null;
            this.userGroups = null;
            this.userOrganizations = null;
        },
        async deleteUser() {
            let self = this;
            this.deletingUser = true
            const {user} = this;
            const response = await deleteAccount(user.id);
            if (!response.isSuccess) {

                this.$store.commit(SET_SNACKBAR, {
                    text: response.errors ? response.errors[0].message : 'There was a problem deleting this user',
                    color: 'error',
                    open: true
                });
            } else {

                this.$store.commit(SET_SNACKBAR, {
                    text: 'User successfully deleted.',
                    color: 'success',
                    open: true
                });
                await self.$router.push('/users')
            }

            this.deletingUser = false
        },
        async onOrgRoleChanged({ organization, organizationAccessTypeId }) {
            // Mirror the change into local state so editUserForm (used by the Edit User
            // dialog) and the userOrganizations array stay in sync with what the
            // shared UserOrganizationsCard component just persisted.
            const orgIndex = this.userOrganizations.findIndex(x => x.id === organization.id);
            if (orgIndex > -1) {
                this.userOrganizations[orgIndex].organizationAccessTypeId = organizationAccessTypeId;
            }
            const editIndex = this.editUserForm.organizations.findIndex(x => x.id === organization.id);
            if (editIndex > -1) {
                this.editUserForm.organizations[editIndex].organizationAccessTypeId = organizationAccessTypeId;
            }
            await this.refreshOwnAvailableOrganizations();
        },
        async onOrgsSaved() {
            // Reload the user (refreshes userOrganizations, groups, editUserForm) and
            // re-hydrate the top-right org switcher if the edited user is self.
            await this.initializeUser();
            await this.refreshOwnAvailableOrganizations();
        },
        async refreshOwnAvailableOrganizations() {
            // The top-right org switcher reads currentAccount.availableOrganizations. Only
            // re-hydrate it when the edited user is the logged-in user.
            if (!this.isEditingSelf) return;
            const currentOrgId = this.$store.getters.selectedOrganizationId;
            if (currentOrgId) {
                await switchSelectedOrganization(currentOrgId);
            }
        },
        async submitEditUserGroups() {
            this.savingUserGroups = true;

            let request = {...updateAccountGroupsModel}

            request.id = this.user.id;
            request.groupIds = this.editUserForm.groupIds;
            const response = await updateAccountGroups(request);

            if (!response.isSuccess) {
                this.savingUser = false;
                return
            }

            await this.initializeUser();

            this.$store.commit(SET_SNACKBAR, {
                text: 'User Successfully Updated',
                color: 'success',
                open: true
            });

            this.savingUserGroups = false;
            this.editingUserGroups = false;
        },
        async submitEditUserForm() {

            if (isCovidAccount() || isPatientAccount()) {
                this.$store.commit(SET_SNACKBAR, {
                    text: 'Covid and Patient Accounts cannot be edited here. Please go to their profile page.',
                    color: 'error',
                    open: true
                });
                return;
            }

            this.savingUser = true;

            const {
                email,
                firstName,
                lastName,
                phoneNumber,
                jobTitle,
                isSafetyCoach,
                professionalTypeId,
                organizationAccessTypeId,
                groupIds,
                organizations,
                isBillingContact,
                isOptimizedSyncEnabled,
                optimizedSyncTimePeriodDays
            } = this.editUserForm;

            let request = {...updateAccountModel}

            let accountOrganizations;
            if (this.editUserForm.organizationIds) {
                let accountId = this.user.id;
                accountOrganizations = this.editUserForm.organizationIds.map(orgId => {
                    let existingOrg = this.editUserForm.organizations.find(o => o.id === orgId);
                    return {
                        accountId: accountId,
                        organizationId: orgId,
                        organizationAccessTypeId: existingOrg?.organizationAccessTypeId || 1
                    };
                });
            }

            request.id = this.user.id;
            request.email = email;
            request.firstName = firstName;
            request.lastName = lastName;
            request.phoneNumber = phoneNumber;
            request.jobTitle = jobTitle;
            request.isSafetyCoach = isSafetyCoach;
            request.professionalTypeId = professionalTypeId;
            request.organizationAccessTypeId = organizationAccessTypeId;
            request.groupIds = groupIds;
            request.accountOrganizations = accountOrganizations;
            request.updateGroups = true;
            request.updateOrganizations = false;
            request.isChangingEmail = false;
            request.isBillingContact = isBillingContact;
            request.isOptimizedSyncEnabled = isOptimizedSyncEnabled;
            request.optimizedSyncTimePeriodDays = optimizedSyncTimePeriodDays;

            if (request.email !== this.user.email) {
                const response = await checkEmailAddress(request.email);
                if (!response.isSuccess) return

                if (response.accountExists) {
                    this.$store.commit(SET_SNACKBAR, {
                        text: 'Email Address Already Exists',
                        color: 'error',
                        open: true
                    })
                    this.savingUser = false;
                    return
                }

                request.isChangingEmail = true;
            }

            const response = await updateAccount(request);

            if (!response.isSuccess) {
                this.savingUser = false;
                return
            }

            // Update groups separately (backend requires separate endpoint)
            if (groupIds && organizationAccessTypeId !== 2) {
                let groupsRequest = {...updateAccountGroupsModel}
                groupsRequest.id = this.user.id;
                groupsRequest.groupIds = groupIds;
                const groupsResponse = await updateAccountGroups(groupsRequest);
                if (!groupsResponse.isSuccess) {
                    this.savingUser = false;
                    return
                }
            }

            await this.initializeUser();

            this.$store.commit(SET_SNACKBAR, {
                text: 'User Successfully Updated',
                color: 'success',
                open: true
            })

            this.savingUser = false;
            this.editUserDialogOpen = false;
        }
    },
    computed: {
        isEditingSelf() {
            if (!this.user || !this.$store.state.currentAccount) return false;
            return this.user.id === this.$store.state.currentAccount.id;
        },
        isEnterpriseView() {
            return this.$store.getters.isEnterpriseAdminOrg;
        },
        canManageOwnOrgRole() {
            return this.isEnterpriseView && this.$store.getters.isOrgAdmin;
        },
        isAdmin() {
            if (!this.user) return false;
            return isAdminOrgAccessType(this.user.selectedOrganizationAccessTypeId);
        },
        isCovidAccount() {
            if (!this.user) return false;
            return isCovidAccount(this.user.accountTypeId);
        },
        isPatientAccount() {
            if (!this.user) return false;
            return isPatientAccount(this.user.accountTypeId);
        },
        fullName() {
            return this.user ? `${this.user.firstName} ${this.user.lastName}` : 'User'
        },
        fields() {
            if (!this.user) return []
            let professionalType = this.user.professionalTypeId ? getProfessionalTypeById(this.user.professionalTypeId) : '-';
            let fields = [
                {
                    label: 'Professional Designation',
                    value: this.user.jobTitle ? this.user.jobTitle : '-'
                },
                {
                    label: 'Email',
                    value: this.user.email
                },
                {
                    label: 'Phone Number',
                    value: this.user.phoneNumber
                },
                {
                    label: 'Medical Credentials',
                    value: professionalType
                },
            ]

            if (this.user.isSafetyCoach){
                fields.push({
                    label: 'Is Safety Coach',
                    value: this.user.isSafetyCoach ? 'Yes' : 'No'
                });
            }

            return fields;
        }
    }
}
</script>

<style scoped>
.system-admin-groups-message {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #e3f2fd;
    border-radius: 4px;
    color: #1565c0;
    font-size: 14px;
}
</style>
